import { isStorageAvailable } from "@shared/utils/storage/cache-storage";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

const HAS_ANDROID_TWA_REFERRER_KEY = "has_android_twa_referrer";

/**
 * Custom hook to detect if UA is from an Android device and if Trusted Web Activity features are supported.
 * The returned value will stay true if the check has been used before within the browser session.
 * Returns null if UA is not an Android device
 * To learn more about the feature detection tricks being used:
 * @see https://blog.chromium.org/2019/02/introducing-trusted-web-activity-for.html
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Navigator/share#browser_compatibility
 * @see https://developer.mozilla.org/en-US/docs/Web/API/notification#browser_compatibility
 * @see https://stackoverflow.com/questions/54580414/how-can-i-detect-if-my-website-is-opened-inside-a-trusted-web-actvity
 */
export const useAndroidTWACheck = (): boolean => {
  const [isTWAReferrer, _setIsTWAReferrer] = useState(null);
  const router = useRouter();

  const setIsTWAReferrer = (value: boolean) => {
    _setIsTWAReferrer(value);
    if (isStorageAvailable("sessionStorage")) {
      sessionStorage.setItem(HAS_ANDROID_TWA_REFERRER_KEY, `${value}`);
    }
  };

  useEffect(() => {
    if (isStorageAvailable("sessionStorage")) {
      return _setIsTWAReferrer(
        sessionStorage.getItem(HAS_ANDROID_TWA_REFERRER_KEY)
      );
    }

    return undefined;
  }, []);

  useEffect(() => {
    if (navigator) {
      const ua = navigator.userAgent.toLowerCase();
      const isAndroid = ua.indexOf("android") > -1;

      if (isAndroid) {
        // Android apps have the option to open links in customTabs with a
        // query param appended to it to state TWA.
        if (router.query?.utm_source === "trusted-web-activity") {
          return setIsTWAReferrer(true);
        }

        // Check for Share API. This API does not work on Android WebViews, but fully supported on browsers.
        if (typeof navigator.canShare === "function" && navigator.canShare()) {
          return setIsTWAReferrer(true);
        }

        // Check for Notification API. This API does not work on Android WebViews, with spotty support on browsers.
        // This should be used as a last resort due to the spotty support on android browsers.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if ("Notification" in window && Notification.maxActions != null) {
          return setIsTWAReferrer(true);
        }

        // If on Android and unsupported, return false
        return setIsTWAReferrer(false);
      }
    }

    return undefined;
  }, [router]);

  return isTWAReferrer;
};
